import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAppContext } from '@prisjakt/ui-app-context';
import PopOver from '@prisjakt/ui-popover';
import RatingHelper from '@prisjakt/ui-rating/src/rating-helper';
import AlternativePriceRow from '../alternative-price-row';
import ExpandButton from '../../components/expand-button';
import ExpandedInfo from '../../components/expanded-info';
import GoToStoreButton from '../../components/go-to-store-button';
import PriceColumn from '../../components/price-column/price-column';
import ReportButton from '../../components/report-button';
import { Award } from '../../../../../../../types';

import { trackExpandPriceRowClick, trackShopLogoClick } from '../../tracking';
import { fetchSotyAwardsByStoreId } from '../../requests';

import * as S from '../styles';
import { IPriceRowProps, ReportRowType } from '../../types';
import { logRevenueEvent, setAntiBotFlag } from '../../utils';
import { ITableLinkProps } from '../types';
import StoreInfoColumn from '../../components/store-info-column';

const PriceRow: React.FC<IPriceRowProps> = props => {
  const {
    alternativePrices,
    brand,
    brandRecommendedRetailer,
    campaign,
    priceDisplay,
    deliveryOptions = [],
    externalUri,
    isInternationalPrice,
    media,
    name,
    onClickOutBounce,
    onPriceClick,
    openInNewWindow,
    price,
    priceId,
    priceType,
    productId,
    productName,
    stock,
    store,
    storeInfo,
    isSplitList = false,
  } = props;
  const [showDisabledInfo, setShowDisabledInfo] = useState<boolean>(false);
  const [expanded, setExpanded] = useState<boolean>(false);
  const [priceRowReport, setPriceRowReport] = useState(ReportRowType.NONE);
  const [sotyBadge, setSotyBadge] = useState<Award | undefined | null>();
  const [reportRowId, setReportRowId] = useState<string | null>(null);
  const priceRow = useRef<HTMLTableRowElement>(null);
  const { t } = useAppContext();

  const featured = Boolean(store?.featured);

  const rating = store?.userReviewSummary?.rating
    ? RatingHelper.convert(store.userReviewSummary.rating, 1)
    : undefined;

  const handleOutSideClick = useCallback(
    e => {
      if (priceRow && priceRow.current && priceRow.current.contains(e.target)) {
        return;
      }
      setShowDisabledInfo(false);
    },
    [priceRow]
  );

  const startBounceTimer = useCallback(
    (id: string) => {
      // Start a timer when a user clicks out on a product
      // if they return to Prisjakt within 15 seconds
      // display an error report button in the price row.

      const startTime = Date.now();

      const onRefocus = () => {
        if (
          Date.now() - startTime > 15000 &&
          document.visibilityState === 'visible'
        ) {
          document.removeEventListener('visibilitychange', onRefocus);
          return;
        }

        if (document.visibilityState === 'visible') {
          setReportRowId(id);

          // Action event for tracking
          onClickOutBounce(productId, priceId);

          document.removeEventListener('visibilitychange', onRefocus);
        }
      };

      document.addEventListener('visibilitychange', onRefocus);
    },
    [onClickOutBounce, priceId, productId, setReportRowId]
  );

  // componentWillUnmount
  useEffect(() => {
    return () => {
      window.removeEventListener('click', handleOutSideClick);
    };
  }, [handleOutSideClick]);

  // Callback for showDisabledInfo state change
  useEffect(() => {
    if (showDisabledInfo) {
      window.addEventListener('click', handleOutSideClick);
    } else {
      window.removeEventListener('click', handleOutSideClick);
    }
  }, [handleOutSideClick, showDisabledInfo]);

  const toggleDisabledInfo = useCallback(() => {
    setShowDisabledInfo(!showDisabledInfo);
  }, [showDisabledInfo]);

  const handleClickOut = useCallback(
    (e, id, isAlternative = false) => {
      setPriceRowReport(
        isAlternative ? ReportRowType.ALTERNATIVE : ReportRowType.DEFAULT
      );
      if (featured) {
        logRevenueEvent(id, productId);
      }

      if (externalUri) {
        if (IS_BROWSER) {
          startBounceTimer(id);
        }

        if (!showDisabledInfo && !e.defaultPrevented) {
          onPriceClick(
            Boolean(store && store.featured),
            undefined,
            isInternationalPrice ? 'international' : '',
            isAlternative,
            isSplitList
          );
        }
      } else {
        e.preventDefault();
        toggleDisabledInfo();
      }
    },
    [
      externalUri,
      isInternationalPrice,
      onPriceClick,
      showDisabledInfo,
      startBounceTimer,
      store,
      toggleDisabledInfo,
      productId,
      isSplitList,
      featured,
    ]
  );

  const tableLinkProps: ITableLinkProps = {
    rel: 'nofollow noopener',
    href: externalUri || undefined,
    onClick: (e: React.MouseEvent) => handleClickOut(e, priceId),
    onMouseDown: setAntiBotFlag,
    onTouchStart: setAntiBotFlag,
    onKeyDown: setAntiBotFlag,
    target: openInNewWindow ? '_blank' : '',
    disabled: !externalUri,
    featured,
  };

  const handleExpandButtonClick = () => {
    setExpanded(!expanded);
    if (!expanded) {
      trackExpandPriceRowClick(productId, productName, store?.id, store?.name);

      if (sotyBadge === undefined && store?.id) {
        fetchSotyAwardsByStoreId(store.id).then(response => {
          if (response?.data?.store?.storeOfTheYearAwards?.length) {
            setSotyBadge(response.data.store.storeOfTheYearAwards[0]);
          } else {
            setSotyBadge(null);
          }
        });
      }
    }
  };

  const handleClickOutAltPrice = useCallback(
    (e, id) => {
      handleClickOut(e, id, true);
    },
    [handleClickOut]
  );

  const closeReportOverlay = useCallback(() => {
    setPriceRowReport(ReportRowType.NONE);
    setReportRowId(null);
  }, [setReportRowId]);

  const renderProductNameColumn = () => (
    <S.Column expand>
      <S.Link alignLeft {...tableLinkProps}>
        <S.ProductNameColumn>
          <S.ProductName light={!featured}>{name}</S.ProductName>
        </S.ProductNameColumn>
      </S.Link>
    </S.Column>
  );

  const renderGoToStoreButton = () => (
    <S.Column>
      {externalUri ? (
        <S.GoToStoreLink {...tableLinkProps}>
          <GoToStoreButton visible disabled={!featured} featured={featured} />
        </S.GoToStoreLink>
      ) : (
        <PopOver
          id={`click-out-${priceId}`}
          open={Boolean(showDisabledInfo)}
          width={250}
          offsetY={-35}
          offsetX={40}
          arrowOffsetY={35}
          position="left"
          label={t('product', 'transparency.prices-more-info-button')}
          onClose={toggleDisabledInfo}
        >
          <S.GoToStoreLink {...tableLinkProps}>
            <GoToStoreButton
              visible={Boolean(externalUri || featured)}
              disabled={Boolean(externalUri && !featured)}
              featured={featured}
            />
          </S.GoToStoreLink>
          <S.TransparencyShopInfo>
            {t('product', 'transparency.store-info')}
          </S.TransparencyShopInfo>
        </PopOver>
      )}
    </S.Column>
  );

  const renderStockAndPrice = () => {
    return (
      <S.Column>
        <S.Link alignRight {...tableLinkProps}>
          <S.PriceAndStockColumn>
            <S.PriceColumnWrapper>
              {campaign && <S.CampaignBadge colors={campaign.media.colors} />}
              {price && (
                <PriceColumn
                  price={price}
                  storeIsFeatured={featured}
                  isCampaign={Boolean(campaign)}
                  align="right"
                  priceDisplay={priceDisplay}
                />
              )}
            </S.PriceColumnWrapper>

            {store?.featured && (
              <S.StockStatusColumn
                status={stock && stock.status}
                statusText={stock && stock.statusText}
                deliveryOptions={deliveryOptions}
                countryCode={store && store.countryCode}
                toneDown={!featured}
                isInternationalPrice={isInternationalPrice}
                priceType={priceType}
                brandRecommendedRetailer={brandRecommendedRetailer}
              />
            )}
          </S.PriceAndStockColumn>
        </S.Link>
      </S.Column>
    );
  };

  return (
    <React.Fragment key={store?.id || 'price-row'}>
      <S.RowWrapper expanded={expanded} featured={featured}>
        <S.Row // Classname is used to track click out
          className="pj-ui-price-row"
          featured={featured}
          noExternalUri={!externalUri}
          showReport={reportRowId === priceId}
          data-test="PriceRow"
          ref={priceRow}
          expanded={expanded}
        >
          <S.LeftColumn>
            <ExpandButton
              expanded={expanded}
              onClick={handleExpandButtonClick}
              title={t('product', 'price.more-info')}
            />
          </S.LeftColumn>

          <S.MiddleColumn featured={featured}>
            <S.Column>
              {store && (
                <S.Link
                  alignLeft
                  {...tableLinkProps}
                  onClick={e => {
                    trackShopLogoClick(store.id);
                    handleClickOut(e, priceId);
                  }}
                >
                  <StoreInfoColumn
                    store={store}
                    hide={expanded}
                    rating={rating}
                  />
                </S.Link>
              )}
            </S.Column>
            {renderProductNameColumn()}
            {renderStockAndPrice()}
            {renderGoToStoreButton()}
          </S.MiddleColumn>
        </S.Row>
        {expanded && store && (
          <S.InfoRow data-test="ColumnExpanded">
            <ExpandedInfo
              brand={brand}
              brandRecommendedRetailer={brandRecommendedRetailer}
              deliveryOptions={deliveryOptions}
              priceId={priceId}
              rating={rating}
              store={store}
              storeInfo={storeInfo}
              sotyBadge={sotyBadge}
            />
          </S.InfoRow>
        )}
        {expanded &&
          alternativePrices &&
          alternativePrices.map(altPrice => {
            if (!altPrice) return null;

            return (
              <React.Fragment key={altPrice.id || 'alternative-price-row'}>
                <AlternativePriceRow
                  key={altPrice.id ? altPrice.id : 'alternative-price-row'}
                  externalUri={externalUri}
                  campaign={campaign}
                  onClickOut={handleClickOutAltPrice}
                  isInternationalPrice={isInternationalPrice}
                  price={altPrice}
                  priceStore={store}
                  productImage={media.first}
                  tableLinkProps={tableLinkProps}
                  priceDisplay={priceDisplay}
                />
                {priceRowReport === ReportRowType.ALTERNATIVE &&
                  reportRowId === altPrice.id && (
                    <S.AltReportRow>
                      <S.Column>
                        <ReportButton
                          priceStore={store}
                          priceId={altPrice.id}
                          priceValue={
                            altPrice.price && altPrice.price.exclShipping
                          }
                          stockStatus={altPrice.stock && altPrice.stock.status}
                          productImage={media.first}
                          onClose={closeReportOverlay}
                          productName={altPrice.name}
                        />
                      </S.Column>
                    </S.AltReportRow>
                  )}
              </React.Fragment>
            );
          })}
      </S.RowWrapper>
      {priceRowReport === ReportRowType.DEFAULT && reportRowId === priceId && (
        <S.ReportRow>
          <S.Column>
            <ReportButton
              priceStore={store}
              priceId={priceId}
              priceValue={price && price.exclShipping}
              stockStatus={stock && stock.status}
              productImage={media.first}
              onClose={closeReportOverlay}
              productName={productName}
            />
          </S.Column>
        </S.ReportRow>
      )}
    </React.Fragment>
  );
};

export default PriceRow;
